import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import * as StampCardService from '../../api/stampCardService';
import * as LoyaltyRewardService from '../../api/loyaltyRewardsService';

import Spinner from '../../components/Spinner';
import AddStampDialog from '../../components/Dialogs/AddStampDialog';
import AddStampSingleUseSuccessDialog from '../../components/Dialogs/AddStampSingleUseSuccessDialog';
import ErrorDialog from '../../components/Dialogs/ErrorDialog';
import useStampCardContext from '../../hooks/useStampCardContext';
import useMerchantContext from '../../hooks/useMerchantContext';
import useNavContext from '../../hooks/useNavContext';

interface RouteParams {
  stampNum: string;
  merchantId: string;
  status: string;
  msg: string;
  stampCardId: string;
}

/**
 * Merchant
 */
export default function RedeemSingleUseStatusScreen() {
  const { stampNum, merchantId, status, msg, stampCardId } =
    useParams<RouteParams>();
  const [isLoading, setIsLoading] = useState(true);
  const [addedStamp, setAddedStamp] = useState(0);
  const [launchAddStampDialog, setLaunchAddStampDialog] = useState(false);
  const [stampSuccessDialog, setStampSuccessDialog] = useState(false);
  const [stampCard, setStampCard] =
    useState<StampCardService.StampCard | null>();
  const [launchAlertDialog, setLaunchAlertDialog] = useState(false);
  const errorMsg = msg === 'undefined' ? 'Qr is invalid' : msg;
  const [milestoneThresholdList, setMilestoneThresholdList] = useState<
    number[]
  >([]);

  const history = useHistory();
  const sc = useStampCardContext();
  const selectedMerchant = useMerchantContext();
  const nav = useNavContext();

  const fetchStampCard = useCallback(async () => {
    try {
      const res = await StampCardService.get(stampCardId);
      setStampCard(res);
      const resMilestone = await LoyaltyRewardService.listMilestone(
        res.loyalty_campaign.id,
      );

      if (resMilestone && resMilestone.length > 0) {
        setMilestoneThresholdList(resMilestone.map((m) => m.threshold));
      }

      setIsLoading(false);
      setStampSuccessDialog(true);
    } catch (e) {
      console.log('Error: Fetch Stampcard from merchant');
    }
  }, [stampCardId]);

  const fetchMemberStampCards = useCallback(async () => {
    if (isLoading && merchantId && sc) {
      try {
        const resStampCardList =
          await StampCardService.listByMerchantId(merchantId);

        if (resStampCardList && resStampCardList.length > 0 && sc) {
          sc.setStampCard({
            selectedStampCardId: resStampCardList[0].id,
            selectedSlotNumber: resStampCardList[0].slotNumber,
            selectedStampCampaignId: resStampCardList[0].loyalty_campaign.id,
          });
        }
        setIsLoading(false);
        setLaunchAlertDialog(true);
      } catch (e) {
        console.log('Error: Fetch Stampcard from merchant');
        setIsLoading(false);
        setLaunchAlertDialog(true);
      }
    }
  }, [merchantId, sc, isLoading]);

  useEffect(() => {
    let init = false;
    if (status === 'success') {
      if (!init && isLoading && stampCardId) {
        fetchStampCard();
      }
    } else {
      if (!init && status === 'fail') {
        // find if user is a member of the merchant by fetching stampcards using merchantId
        fetchMemberStampCards();
      }
    }

    return () => {
      init = true;
    };
  }, [stampCardId, isLoading, fetchStampCard, status, fetchMemberStampCards]);

  const handleClose = async (
    stampCardId: string,
    slotNumber: number,
    stampCampaignId: string,
    status: string,
  ) => {
    // check if stampcard completed, get new stampCard id
    if (status !== 'completed') {
      if (sc) {
        sc.setStampCard({
          selectedStampCardId: stampCardId,
          selectedSlotNumber: slotNumber,
          selectedStampCampaignId: stampCampaignId,
        });
      }
      setStampSuccessDialog(false);
      selectedMerchant?.setMerchant({ merchantId });
      nav?.setNav({ menuNav: 'home' });
      history.replace(`/merchant`);
      window.location.reload();
    } else {
      try {
        const resNewStampCard =
          await StampCardService.getStampCardByCampaignAndSlot(
            stampCampaignId,
            slotNumber,
          );

        if (resNewStampCard.id !== stampCardId) {
          if (sc) {
            sc.setStampCard({
              selectedStampCardId: resNewStampCard.id,
              selectedSlotNumber: resNewStampCard.slotNumber,
              selectedStampCampaignId: resNewStampCard.loyalty_campaign.id,
            });
          }
        } else {
          if (sc) {
            sc.setStampCard({
              selectedStampCardId: stampCardId,
              selectedSlotNumber: slotNumber,
              selectedStampCampaignId: stampCampaignId,
            });
          }
        }
        setStampSuccessDialog(false);
        selectedMerchant?.setMerchant({ merchantId });
        nav?.setNav({ menuNav: 'home' });
        history.replace(`/merchant`);
        window.location.reload();
      } catch (e) {
        console.log('Error: Fetch Stampcard from merchant');
        if (sc) {
          sc.setStampCard({
            selectedStampCardId: stampCardId,
            selectedSlotNumber: slotNumber,
            selectedStampCampaignId: stampCampaignId,
          });
        }
        setStampSuccessDialog(false);
        selectedMerchant?.setMerchant({ merchantId });
        nav?.setNav({ menuNav: 'home' });
        history.replace(`/merchant`);
        window.location.reload();
      }
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 200,
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 200,
      }}
    >
      {launchAlertDialog && merchantId && (
        <ErrorDialog
          open={launchAlertDialog}
          onClose={() => {
            setLaunchAlertDialog(false);
            selectedMerchant?.setMerchant({ merchantId });
            nav?.setNav({ menuNav: 'home' });
            history.replace(`/merchant`);
            window.location.reload();
          }}
          onConfirm={() => {
            setLaunchAlertDialog(false);
            selectedMerchant?.setMerchant({ merchantId });
            nav?.setNav({ menuNav: 'home' });
            history.replace(`/merchant`);
            window.location.reload();
          }}
          title="Error"
          body={errorMsg}
          confirmButtonText="Dismiss"
        />
      )}
      {stampCard && merchantId && (
        <>
          <AddStampDialog
            stampCardId={stampCard.id}
            open={launchAddStampDialog}
            onClose={() => {
              setLaunchAddStampDialog(false);
              selectedMerchant?.setMerchant({ merchantId });
              nav?.setNav({ menuNav: 'home' });
              history.replace(`/merchant`);
              window.location.reload();
            }}
            confirmRedeem={(addedStamp: number) => {
              setAddedStamp(addedStamp);
              setLaunchAddStampDialog(false);
              setStampSuccessDialog(true);
            }}
          />
          <AddStampSingleUseSuccessDialog
            isComplete={stampCard.quantity === stampCard.redemptionCost}
            isMilestone={
              milestoneThresholdList.length > 0
                ? milestoneThresholdList.includes(stampCard.quantity)
                : false
            }
            open={stampSuccessDialog}
            onClose={() => {
              handleClose(
                stampCard.id,
                stampCard.slotNumber,
                stampCard.loyalty_campaign.id,
                stampCard.status,
              );
            }}
            campaignId={stampCard.loyalty_campaign.id}
            quantity={stampCard.quantity}
            isMultipleStamp={
              addedStamp === 0 ? parseInt(stampNum) > 1 : addedStamp > 1
            }
            stampAdded={addedStamp === 0 ? stampNum : addedStamp.toString()}
            handleAddMoreStamp={() => {
              if (sc) {
                sc.setStampCard({
                  selectedStampCardId: stampCard.id,
                  selectedSlotNumber: stampCard.slotNumber,
                  selectedStampCampaignId: stampCard.loyalty_campaign.id,
                });
              }
              setStampSuccessDialog(false);
              setLaunchAddStampDialog(true);
            }}
          />
        </>
      )}
    </div>
  );
}
